import React, { FormEvent, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Logo } from '../Logo';
import { useAuth } from '../../contexts/AuthContext';

interface RegisterFormProps {
  redirectTo: string;
  initialEmail?: string;
  autoFocus?: boolean;
  showBranding?: boolean;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  redirectTo,
  initialEmail = '',
  autoFocus = true,
  showBranding = true,
}) => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);

  const buildLoginLink = (nextEmail: string) => {
    const params = new URLSearchParams({ redirect: redirectTo });

    if (nextEmail) {
      params.set('email', nextEmail);
    }

    return `/login?${params.toString()}`;
  };

  const validateFirstName = (value: string): boolean => {
    if (!value.trim()) {
      setFieldErrors((prev) => ({ ...prev, firstName: t('auth.register.firstNameRequired') }));
      return false;
    }

    if (value.trim().length < 2) {
      setFieldErrors((prev) => ({ ...prev, firstName: t('auth.register.firstNameMin') }));
      return false;
    }

    setFieldErrors((prev) => ({ ...prev, firstName: '' }));
    return true;
  };

  const validateLastName = (value: string): boolean => {
    if (!value.trim()) {
      setFieldErrors((prev) => ({ ...prev, lastName: t('auth.register.lastNameRequired') }));
      return false;
    }

    if (value.trim().length < 2) {
      setFieldErrors((prev) => ({ ...prev, lastName: t('auth.register.lastNameMin') }));
      return false;
    }

    setFieldErrors((prev) => ({ ...prev, lastName: '' }));
    return true;
  };

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!value) {
      setFieldErrors((prev) => ({ ...prev, email: t('auth.register.emailRequired') }));
      return false;
    }

    if (!emailRegex.test(value)) {
      setFieldErrors((prev) => ({ ...prev, email: t('auth.register.invalidEmail') }));
      return false;
    }

    setFieldErrors((prev) => ({ ...prev, email: '' }));
    return true;
  };

  const validatePassword = (value: string): boolean => {
    if (!value) {
      setFieldErrors((prev) => ({ ...prev, password: t('auth.register.passwordRequired') }));
      return false;
    }

    if (value.length < 6) {
      setFieldErrors((prev) => ({ ...prev, password: t('auth.register.passwordMin') }));
      return false;
    }

    setFieldErrors((prev) => ({ ...prev, password: '' }));
    return true;
  };

  const validateConfirmPassword = (value: string): boolean => {
    if (!value) {
      setFieldErrors((prev) => ({ ...prev, confirmPassword: t('auth.register.confirmPasswordRequired') }));
      return false;
    }

    if (value !== password) {
      setFieldErrors((prev) => ({ ...prev, confirmPassword: t('auth.register.passwordsMismatch') }));
      return false;
    }

    setFieldErrors((prev) => ({ ...prev, confirmPassword: '' }));
    return true;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    const isFirstNameValid = validateFirstName(firstName);
    const isLastNameValid = validateLastName(lastName);
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);

    if (
      !isFirstNameValid ||
      !isLastNameValid ||
      !isEmailValid ||
      !isPasswordValid ||
      !isConfirmPasswordValid
    ) {
      return;
    }

    setLoading(true);

    try {
      await register(email, password, firstName.trim(), lastName.trim());
      navigate(redirectTo);
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.register.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        {showBranding && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Logo size="large" />
          </Box>
        )}
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          {t('auth.register.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auth.register.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('common.fields.firstName')}
              value={firstName}
              onChange={(event) => {
                setFirstName(event.target.value);
                if (fieldErrors.firstName) {
                  validateFirstName(event.target.value);
                }
              }}
              onBlur={() => validateFirstName(firstName)}
              error={!!fieldErrors.firstName}
              helperText={fieldErrors.firstName}
              required
              autoComplete="given-name"
              autoFocus={autoFocus}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('common.fields.lastName')}
              value={lastName}
              onChange={(event) => {
                setLastName(event.target.value);
                if (fieldErrors.lastName) {
                  validateLastName(event.target.value);
                }
              }}
              onBlur={() => validateLastName(lastName)}
              error={!!fieldErrors.lastName}
              helperText={fieldErrors.lastName}
              required
              autoComplete="family-name"
              disabled={loading}
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label={t('common.fields.email')}
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (fieldErrors.email) {
              validateEmail(event.target.value);
            }
          }}
          onBlur={() => validateEmail(email)}
          error={!!fieldErrors.email}
          helperText={fieldErrors.email}
          margin="normal"
          required
          autoComplete="email"
          disabled={loading}
        />

        <TextField
          fullWidth
          label={t('common.fields.password')}
          type="password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            if (fieldErrors.password) {
              validatePassword(event.target.value);
            }
            if (confirmPassword && fieldErrors.confirmPassword) {
              validateConfirmPassword(confirmPassword);
            }
          }}
          onBlur={() => validatePassword(password)}
          error={!!fieldErrors.password}
          helperText={fieldErrors.password}
          margin="normal"
          required
          autoComplete="new-password"
          disabled={loading}
        />

        <TextField
          fullWidth
          label={t('auth.register.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
            if (fieldErrors.confirmPassword) {
              validateConfirmPassword(event.target.value);
            }
          }}
          onBlur={() => validateConfirmPassword(confirmPassword)}
          error={!!fieldErrors.confirmPassword}
          helperText={fieldErrors.confirmPassword}
          margin="normal"
          required
          autoComplete="new-password"
          disabled={loading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            mt: 3,
            mb: 2,
            py: 1.5,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #4A7C59 0%, #355A40 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #355A40 0%, #2A4833 100%)',
            },
          }}
        >
          {loading ? t('auth.register.submitting') : t('auth.register.submit')}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('auth.register.hasAccount')}{' '}
            <Link
              component={RouterLink}
              to={buildLoginLink(email)}
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('auth.register.signIn')}
            </Link>
          </Typography>
        </Box>
      </form>
    </Box>
  );
};

export default RegisterForm;